import React from 'react';
import { Box, Typography, Stack, Chip } from '@mui/material';
import {
    fmtPct, fmtDelta, rateClass, trajectory, RECENT,
} from './format';
import { lastN, priorN, linregSlope } from './aggregation';

const MetricCard = ({ label, value, valueClass, sub }) => (
    <Box className="sh-card sh-card-tight">
        <Typography className="sh-metric-label">{label}</Typography>
        <Typography component="div" className={`sh-metric-value ${valueClass}`}>
            {value}
        </Typography>
        <Box className="sh-metric-sub">{sub}</Box>
    </Box>
);

const Delta = ({ d }) => {
    const f = fmtDelta(d);
    return <span className={`sh-${f.cls}`}>{f.text}</span>;
};

// Tiny delta-bar — direction icon + colored magnitude bar. Replaces the
// "Provider-run: Stable (+0.45 pp). · Client-night: ..." sentence.
const DeltaBar = ({ label, delta, dir }) => {
    const mag = Math.min(Math.abs(delta || 0), 10) / 10;   // saturate at ±10pp
    const f = fmtDelta(delta);
    const positive = (delta || 0) >= 0;
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted, #888)' }}>{label}</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: dir.color }}>{dir.label}</Typography>
            </Box>
            <Box sx={{ position: 'relative', height: 8, background: '#eee', borderRadius: 4, mt: 0.5, overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: 1, background: '#bbb', zIndex: 1 }} />
                <Box sx={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: positive ? '50%' : `${50 - mag * 50}%`,
                    width: `${mag * 50}%`,
                    background: dir.color,
                    transition: 'all 0.25s',
                }} />
            </Box>
            <Typography className={`sh-${f.cls}`} sx={{ fontSize: 11, mt: 0.5 }}>{f.text}</Typography>
        </Box>
    );
};

const TldrSection = ({ data, agg, state }) => {
    const { runRate, clientNightRate, systemClean } = agg;

    const last7run = lastN(runRate, 7), prior7run = priorN(runRate, 7);
    const last14run = lastN(runRate, 14), prior14run = priorN(runRate, 14);
    const last7cn = lastN(clientNightRate, 7), prior7cn = priorN(clientNightRate, 7);
    const last14cn = lastN(clientNightRate, 14), prior14cn = priorN(clientNightRate, 14);
    const last7sys = lastN(systemClean, 7), prior7sys = priorN(systemClean, 7);
    const last14sys = lastN(systemClean, 14), prior14sys = priorN(systemClean, 14);

    const runSlope = linregSlope(runRate);
    const cnSlope = linregSlope(clientNightRate);
    const sysSlope = linregSlope(systemClean);

    const recentSys = systemClean.slice(Math.max(0, systemClean.length - RECENT));
    const cleanNights14 = recentSys.filter((x) => x === 100).length;
    const totalNights14 = recentSys.filter((x) => x != null && !isNaN(x)).length;

    const wTag = state.weighting === 'equal' ? '' : ` (${state.weighting}-weighted)`;

    const runDir = trajectory(last14run - prior14run);
    const cnDir = trajectory(last14cn - prior14cn);
    const sysDir = trajectory(last14sys - prior14sys);

    return (
        <Box className="sh-card">
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography className="sync-health-section-title">TL;DR — last 7 days</Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted, #888)' }}>
                    {`${data.dates[0]} → ${data.dates[data.dates.length - 1]} (${data.dates.length} nights)`}
                </Typography>
            </Stack>

            <Box className="sh-tldr-cards">
                <MetricCard
                    label="Provider-run rate"
                    value={fmtPct(last7run)}
                    valueClass={`sh-${rateClass(last7run)}`}
                    sub={<>integration reliability{wTag} · <Delta d={last7run - prior7run} /> vs prior week</>}
                />
                <MetricCard
                    label="Client-night clean rate"
                    value={fmtPct(last7cn)}
                    valueClass={`sh-${rateClass(last7cn)}`}
                    sub={<>stakeholder experience{wTag} · <Delta d={last7cn - prior7cn} /> vs prior week</>}
                />
                <MetricCard
                    label="System-night clean rate"
                    value={fmtPct(last7sys)}
                    valueClass={`sh-${rateClass(last7sys)}`}
                    sub={
                        <>
                            {cleanNights14}/{totalNights14} fully clean in last {RECENT}d ·{' '}
                            <Delta d={last7sys - prior7sys} />{' '}
                            <span style={{ color: '#888', fontSize: 10 }}>(weighting doesn't apply)</span>
                        </>
                    }
                />
            </Box>

            <Box className="sh-card sh-card-tight" sx={{ mt: 1.5 }}>
                <Typography className="sh-metric-label">Direction — last 14d vs prior 14d</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 1 }}>
                    <DeltaBar label="Provider-run" delta={last14run - prior14run} dir={runDir} />
                    <DeltaBar label="Client-night" delta={last14cn - prior14cn} dir={cnDir} />
                    <DeltaBar label="System-night" delta={last14sys - prior14sys} dir={sysDir} />
                </Box>
                <Box sx={{ mt: 1, fontSize: 11, color: 'var(--color-text-muted, #888)' }}>
                    Slopes across {data.dates.length} nights · run {runSlope >= 0 ? '+' : ''}{runSlope.toFixed(2)} · cn {cnSlope >= 0 ? '+' : ''}{cnSlope.toFixed(2)} · sys {sysSlope >= 0 ? '+' : ''}{sysSlope.toFixed(2)} pp/night
                </Box>
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Clients to watch:</Typography>
                {agg.watchClients.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: '#888' }}>—</Typography>
                ) : (
                    agg.watchClients.map((c) => (
                        <Chip key={c} label={c} size="small" sx={{ background: '#fdf2ee', color: '#e76f51' }} />
                    ))
                )}
            </Stack>
            <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Providers to watch:</Typography>
                {agg.watchProviders.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: '#888' }}>—</Typography>
                ) : (
                    agg.watchProviders.map((p) => (
                        <Chip key={p} label={p} size="small" sx={{ background: '#fdf2ee', color: '#e76f51' }} />
                    ))
                )}
            </Stack>
        </Box>
    );
};

export default TldrSection;
